import React from "react";
import { ReactComponent as CloudIcon } from "@assets/Icons/IconConnectivityKontrol.svg";
import { resolveCloudProviderLogo } from "kontrolEdge/helpers/cloudProviderLogo";
import { formatSnapshotClock, formatSnapshotTime } from "kontrolEdge/components/SiteLatencyMatrix/siteLatencyLookup";
import { ServiceHealth } from "./overviewModel";

/**
 * What the map and the site diagram draw the same way: a site's border, a
 * line's colour by health, and the legend. One place, so the two views of the
 * same topology cannot drift apart.
 */

export const PILL_BORDER: Record<ServiceHealth, string> = {
  up: "border-semantic-border-primary",
  unknown: "border-semantic-border-primary",
  warning: "border-semantic-fg-warning",
  down: "border-semantic-fg-danger",
};

export const HEALTH_STROKE: Record<ServiceHealth, string> = {
  up: "stroke-semantic-bg-brand-bold",
  warning: "stroke-semantic-fg-warning",
  down: "stroke-semantic-fg-danger",
  unknown: "stroke-semantic-fg-secondary",
};

export const HEALTH_FILL: Record<ServiceHealth, string> = {
  up: "fill-semantic-bg-brand-bold",
  warning: "fill-semantic-fg-warning",
  down: "fill-semantic-fg-danger",
  unknown: "fill-semantic-fg-secondary",
};

/**
 * A CPE on the map: the router of the CPE menu entry, on a 16-unit box
 * centred at the origin. Our colour, but a router rather than a ring, so it
 * never passes for one of our sites.
 */
export const CPE_GLYPH_PATH =
  "M2.5 4H13.5C14.3284 4 15 4.67157 15 5.5V10.5C15 11.3284 14.3284 12 13.5 12H2.5C1.67157 12 1 11.3284 1 10.5V5.5C1 4.67157 1.67157 4 2.5 4ZM3 7H4.6V9H3V7ZM5.4 7H7V9H5.4V7ZM7.8 7H9.4V9H7.8V7ZM10.2 7H11.8V9H10.2V7ZM13.2 8.7C13.5866 8.7 13.9 8.3866 13.9 8C13.9 7.6134 13.5866 7.3 13.2 7.3C12.8134 7.3 12.5 7.6134 12.5 8C12.5 8.3866 12.8134 8.7 13.2 8.7Z";

export function CpeGlyph({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" className={className} aria-hidden>
      <path d={CPE_GLYPH_PATH} fillRule="evenodd" fill="currentColor" />
    </svg>
  );
}

const LEGEND_PLACEMENT = {
  overlay: "absolute bottom-3 left-3 rounded-md border border-semantic-border-primary bg-semantic-bg-primary px-3 py-1.5",
  // Under the drawing, for a phone: over it, the legend and the zoom controls fought for the bottom edge.
  inline: "flex-wrap px-1",
} as const;

export function TopologyLegend({
  hasLinks = true,
  hasClouds,
  hasCpes = false,
  latencyMeasuredAt,
  placement = "overlay",
  className = "",
}: {
  hasLinks?: boolean;
  hasClouds: boolean;
  hasCpes?: boolean;
  /**
   * The DCI captions carry RTT: when it was measured, or null when the
   * snapshot has no time. Undefined when they carry none.
   */
  latencyMeasuredAt?: string | null;
  placement?: keyof typeof LEGEND_PLACEMENT;
  className?: string;
}) {
  return (
    <div
      className={`flex items-center gap-3 text-[11px] text-semantic-text-secondary ${LEGEND_PLACEMENT[placement]} ${className}`}
    >
      {hasLinks && (
        <span className="flex items-center gap-1.5">
          <span className="h-0.5 w-4 rounded bg-semantic-bg-brand-bold" aria-hidden /> DCI
        </span>
      )}
      {hasClouds && (
        <span className="flex items-center gap-1.5">
          <span className="w-4 border-t-2 border-dashed border-semantic-bg-brand-bold" aria-hidden /> Cloud
        </span>
      )}
      {hasCpes && (
        <>
          <span className="flex items-center gap-1.5">
            <CpeGlyph className="h-4 w-4 text-semantic-bg-brand-bold" /> CPE
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-4 border-t-2 border-dotted border-semantic-text-secondary" aria-hidden /> Backhaul
          </span>
        </>
      )}
      <span className="flex items-center gap-1.5">
        <span className="h-2 w-2 rounded-full bg-semantic-fg-warning" aria-hidden /> Attention
      </span>
      {latencyMeasuredAt !== undefined && <LatencyLegendItem measuredAt={latencyMeasuredAt} />}
    </div>
  );
}

/** One short item for the RTT on the DCI captions; the full wording is in its tooltip. */
function LatencyLegendItem({ measuredAt }: { measuredAt: string | null }) {
  const clock = formatSnapshotClock(measuredAt);
  const when = formatSnapshotTime(measuredAt);
  return (
    <span
      className="whitespace-nowrap"
      title={`Round-trip time between sites, lowest across their routers${when ? `. Measured ${when}` : ""}.`}
    >
      ms = RTT{clock ? ` · ${clock}` : ""}
    </span>
  );
}

/**
 * The disc behind a cloud's mark. White under a vendor logo in both themes:
 * the logos are drawn for a light ground (AWS's wordmark is near-black).
 */
export function cloudPlateClass(cloud: string): string {
  return resolveCloudProviderLogo(cloud) ? "fill-white" : "fill-semantic-bg-primary";
}

/** A cloud node's mark: the vendor's logo when we hold one, the generic cloud otherwise. */
export function CloudMark({
  cloud,
  className,
  style,
}: {
  cloud: string;
  /** Placement and entrance classes; the size comes from here. */
  className: string;
  style: React.CSSProperties;
}) {
  const logo = resolveCloudProviderLogo(cloud);
  if (logo) {
    return <img src={logo} alt="" aria-hidden className={`h-5 w-5 object-contain ${className}`} style={style} />;
  }
  return <CloudIcon className={`h-4 w-4 text-semantic-text-brand ${className}`} style={style} aria-hidden />;
}
